import { useContext, useEffect, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { enqueueSnackbar } from 'notistack'

interface CheckOutDeviationDialogProps {
  open: boolean
  eventId: string | null
  onClose: () => void
  onCheckedOut: () => void
}

function CheckOutDeviationDialog({ open, eventId, onClose, onCheckedOut }: CheckOutDeviationDialogProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { context } = useContext(EbAuthenticatedAppContext)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setNote('')
  }, [open])

  const canSubmit = note.trim().length > 0 && !submitting && eventId !== null

  const handleSubmit = async () => {
    if (!canSubmit || eventId === null) return
    setSubmitting(true)
    try {
      const response = await context.restApiClient
        .buildRequest(`/api/event/${encodeURIComponent(eventId)}/deviation/check-out`)
        .serializeToJsonContent({ note: note.trim() })
        .postAsync()
      if (response.error) {
        enqueueSnackbar(<Typography variant="body2">Klarte ikke sjekke ut avviket. Prøv igjen.</Typography>, { variant: 'error' })
        return
      }
      enqueueSnackbar(<Typography variant="body2">Avvik sjekket ut.</Typography>, { variant: 'success' })
      onCheckedOut()
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>Sjekk ut avvik</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Skriv et notat om hvordan avviket ble behandlet. Notatet lagres sammen med ditt navn og tidspunkt.
          </Typography>
          <TextField
            required
            multiline
            minRows={isMobile ? 5 : 3}
            label="Notat ved utsjekking"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: isMobile ? 1.5 : 1, gap: isMobile ? 1 : 0 }}>
        <Button onClick={onClose} disabled={submitting} fullWidth={isMobile} size={isMobile ? 'large' : 'medium'}>
          Avbryt
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit} fullWidth={isMobile} size={isMobile ? 'large' : 'medium'}>
          Sjekk ut
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CheckOutDeviationDialog
